/**
 * Mirror the backend auto-train bookkeeping (optuneutils.auto_train) so the
 * docked auto-train card and toasts react to generation / agent WebSocket
 * events without waiting for a usecase refetch. The server stays
 * authoritative — a delayed refetch reconciles whatever it decided.
 */

const GENERATION_TERMINAL_STATUSES = new Set(['completed', 'failed', 'cancelled']);
const RETRIEVAL_TERMINAL_STATUSES = new Set(['completed', 'failed', 'agent_failure']);
const UPLOAD_TERMINAL_STATUSES = new Set(['completed', 'failed', 'cancelled']);

export function isTerminalAutoTrainFlowStatus(flowType, status) {
    if (flowType === 'generation') return GENERATION_TERMINAL_STATUSES.has(status);
    if (flowType === 'upload') return UPLOAD_TERMINAL_STATUSES.has(status);
    return RETRIEVAL_TERMINAL_STATUSES.has(status);
}

const flowKey = (flow) =>
    `${flow.flow_type}:${flow.session_id || ''}:${flow.dataset_name || ''}`;

/**
 * Record one terminal flow on the local pending copy (idempotent, like the
 * backend's $addToSet). Returns the same reference when nothing applies.
 *
 * @param {Object|null} pending - `usecase.auto_train_pending`
 * @param {{flowType: 'generation'|'retrieval'|'upload', datasetName?: string, sessionId?: string, status: string, error?: string}} flow
 */
const FLOW_TYPE_ERROR_LABELS = {
    generation: 'Generation',
    retrieval: 'Retrieval Agent',
    upload: 'Upload',
};

export function applyAutoTrainFlowTerminal(pending, {
    flowType,
    datasetName,
    sessionId,
    status,
    error,
} = {}) {
    if (!pending || !flowType) return pending;
    if (!isTerminalAutoTrainFlowStatus(flowType, status)) return pending;

    const entry = {
        flow_type: flowType,
        dataset_name: datasetName,
        status,
    };
    if (sessionId) entry.session_id = sessionId;
    const succeeded = status === 'completed';
    if (!succeeded) entry.error = error || `${FLOW_TYPE_ERROR_LABELS[flowType] || 'Data flow'} ${status}`;

    const listName = succeeded ? 'succeeded_flows' : 'failed_flows';
    const existing = pending[listName] || [];
    if (existing.some((recorded) => flowKey(recorded) === flowKey(entry))) {
        return pending;
    }
    return { ...pending, [listName]: [...existing, entry] };
}

/** True when at least one flow recorded on the pending copy succeeded. */
export function hasSucceededAutoTrainFlow(pending) {
    return (pending?.succeeded_flows || []).length > 0;
}

/**
 * Phase of the post-flows settle window for the docked auto-train card.
 *
 * While `triggering`, the backend settle thread persists its per-poll record
 * counts onto `pending.settle` (see optuneutils.auto_train). The card shows
 * "Finalizing data" while rows are still landing, and "Starting training"
 * once every dataset has records and the counts stopped moving for at least
 * one poll.
 *
 * @param {Object|null} pending - `usecase.auto_train_pending`
 * @returns {{phase: 'finalizing'|'starting', datasets: Array<{name: string, is_benchmark?: boolean, records: number}>}|null}
 *   `null` when the backend is not triggering. `datasets` is empty until the
 *   settle thread wrote its first poll.
 */
export function resolveTriggeringPhase(pending) {
    if (!pending?.triggering) return null;
    const settle = pending.settle || null;
    const datasets = Array.isArray(settle?.datasets) ? settle.datasets : [];
    const allHaveData = datasets.length > 0 && datasets.every((dataset) => Number(dataset?.records) > 0);
    const stable = Number(settle?.stable_polls) >= 1;
    return {
        phase: allHaveData && stable ? 'starting' : 'finalizing',
        datasets,
    };
}
